import clsx from 'clsx'
import { Button } from 'component/Button'
import { DropdownProvider, useDropdownConstructor } from 'component/Dropdown/DropdownContext'
import { DropdownMenu } from 'component/Dropdown/DropdownMenu'
import { useClickOutside } from 'hook/useClickOutside'
import { useLatestCallback } from 'hook/useLatestCallback'
import React, { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import css from './Dropdown.module.scss'

type Props<O> = {
  children: ReactNode
  content?: ReactNode
  title?: string
  options?: ReadonlyArray<O>
  width?: number | string
  height?: string | number
  renderOption?: (option: O) => ReactElement
  onSelect?: (option: O) => void
  onOpen?: (open: boolean) => void
  buttonPadSmall?: boolean
}

type Position = {
  left: number
  top: number
  width: number
}

export const Dropdown = <O, >(props: Props<O>) => {
  const {
    children,
    content,
    title,
    options,
    width,
    height,
    renderOption,
    onSelect,
    onOpen,
    buttonPadSmall,
  } = props
  const store = useDropdownConstructor({ onOpen })
  const { open, setOpen } = store
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState<Position>({
    left: 0,
    top: 0,
    width: 0,
  })
  const [dropdownHeight, setDropdownHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const getDialogElement = () => {
    return document.body
  }

  const onSelectInner = useLatestCallback((option: O) => {
    if (onSelect) onSelect(option)
  })

  const onOpenInner = useLatestCallback(() => {
    setOpen(!open)
  })

  const onClose = useLatestCallback(() => {
    setOpen(false)
  })

  const clickOutsideRef = useClickOutside(() => {
    if (open) {
      onClose()
    }
  })

  useEffect(() => {
    if (!open) return

    const handleClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      const dialog = buttonRef.current?.closest('dialog[open]')

      if (dialog && target === dialog) {
        onClose()
      }
    }

    const eventType = 'ontouchstart' in window ? 'touchstart' : 'mousedown'
    document.addEventListener(eventType, handleClick)
    return () => document.removeEventListener(eventType, handleClick)
  }, [open, onClose])

  const getElementStyles = (element: HTMLElement) => {
    const styles = window.getComputedStyle(element)
    return {
      borderLeft: parseFloat(styles.borderLeftWidth) || 0,
      borderRight: parseFloat(styles.borderRightWidth) || 0,
      paddingLeft: parseFloat(styles.paddingLeft) || 0,
      paddingRight: parseFloat(styles.paddingRight) || 0,
    }
  }

  const calculatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()

      const tempWrapper = document.createElement('div')
      tempWrapper.className = css.wrapper
      document.body.appendChild(tempWrapper)

      const tempInner = document.createElement('div')
      tempInner.className = css.inner
      tempWrapper.appendChild(tempInner)

      const wrapperStyles = getElementStyles(tempWrapper)
      const innerStyles = getElementStyles(tempInner)

      const borderWidth = wrapperStyles.borderLeft + wrapperStyles.borderRight
      const paddingWidth = innerStyles.paddingLeft + innerStyles.paddingRight
      const totalExtraWidth = borderWidth + paddingWidth

      document.body.removeChild(tempWrapper)

      let dropdownWidth = rect.width
      if (options) {
        // Для опций: ширина дропдауна = ширина кнопки + только бордеры дропдауна
        // Padding дропдауна компенсирует padding кнопки
        dropdownWidth = rect.width + wrapperStyles.borderLeft + wrapperStyles.borderRight

        const content_width = contentRef.current?.getBoundingClientRect().width
        if (content_width) dropdownWidth = content_width
      } else if (content) {
        const contentWidth =
          typeof width === 'number' ? width : Math.max(rect.width, 120)
        dropdownWidth = contentWidth + totalExtraWidth
      } else {
        dropdownWidth += totalExtraWidth
      }

      let left = rect.left

      const wouldOverflowRight = left + dropdownWidth > window.innerWidth - 32
      if (wouldOverflowRight) {
        // Если дропдаун не помещается справа, выравниваем по правому краю кнопки
        left = rect.right - dropdownWidth
      }

      const wouldOverflowLeft = left < 16
      if (wouldOverflowLeft) {
        left = 16
      }

      const availableHeightBelow = window.innerHeight - rect.bottom - 16
      const availableHeightAbove = rect.top - 16
      let contentHeight = 200
      if (options) {
        contentHeight = options.length * 30 + 12
      } else if (content) {
        contentHeight = 200
      }
      let optimalHeight = contentHeight
      let openUpward = false
      if (contentHeight <= availableHeightBelow) {
        optimalHeight = contentHeight
        openUpward = false
      } else if (contentHeight <= availableHeightAbove) {
        optimalHeight = contentHeight
        openUpward = true
      } else if (availableHeightAbove > availableHeightBelow) {
        optimalHeight = availableHeightAbove
        openUpward = true
      } else {
        optimalHeight = availableHeightBelow
        openUpward = false
      }
      optimalHeight = Math.max(0, Math.min(optimalHeight, 400))
      let usedHeight = dropdownHeight && open ? dropdownHeight : optimalHeight

      let actualHeight = usedHeight
      if (openUpward && usedHeight > availableHeightAbove) {
        actualHeight = availableHeightAbove
      } else if (!openUpward && usedHeight > availableHeightBelow) {
        actualHeight = availableHeightBelow
      }

      let dropdownTop
      if (openUpward) {
        dropdownTop = rect.top - 8 - actualHeight
      } else {
        dropdownTop = rect.bottom + 8
      }
      if (dropdownTop < 16) dropdownTop = 16

      setPosition({ left, top: dropdownTop, width: dropdownWidth })
    }
  }, [options, content, width, dropdownHeight, open])

  useEffect(() => {
    if (open) {
      calculatePosition()
    }
  }, [open, calculatePosition])

  useEffect(() => {
    const handleResize = () => {
      if (open) {
        calculatePosition()
      }
    }

    const handleScroll = (event: Event) => {
      if (!open) return

      const target = event.target as Element
      const dropdownElement = contentRef.current

      if (dropdownElement && !dropdownElement.contains(target)) {
        onClose()
      }
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open, calculatePosition, onClose])

  useEffect(() => {
    if (open && contentRef.current) {
      const timer = setTimeout(() => {
        const firstChild = contentRef.current?.firstElementChild as HTMLElement
        let contentHeight = 0

        if (firstChild) {
          const originalHeight = firstChild.style.height
          firstChild.style.height = 'auto'
          contentHeight = firstChild.scrollHeight
          firstChild.style.height = originalHeight
        }

        if (contentHeight > 0) {
          const rect = buttonRef.current?.getBoundingClientRect()
          if (rect) {
            const availableHeightBelow = window.innerHeight - rect.bottom - 16
            const availableHeightAbove = rect.top - 16

            let optimalHeight = contentHeight
            if (contentHeight <= availableHeightBelow) {
              optimalHeight = contentHeight
            } else if (contentHeight <= availableHeightAbove) {
              optimalHeight = contentHeight
            } else if (availableHeightAbove > availableHeightBelow) {
              optimalHeight = availableHeightAbove
            } else {
              optimalHeight = availableHeightBelow
            }
            optimalHeight = Math.max(0, Math.min(optimalHeight, 400))

            if (optimalHeight !== dropdownHeight) {
              setDropdownHeight(optimalHeight)
              if (contentRef.current) {
                contentRef.current.style.maxHeight = `${optimalHeight}px`
              }

              setTimeout(() => {
                if (open && buttonRef.current) {
                  const rect = buttonRef.current.getBoundingClientRect()
                  const availableHeightBelow =
                    window.innerHeight - rect.bottom - 16
                  const availableHeightAbove = rect.top - 16

                  let openUpward = false
                  if (optimalHeight <= availableHeightBelow) {
                    openUpward = false
                  } else if (optimalHeight <= availableHeightAbove) {
                    openUpward = true
                  } else if (availableHeightAbove > availableHeightBelow) {
                    openUpward = true
                  } else {
                    openUpward = false
                  }

                  let actualHeight = optimalHeight
                  if (openUpward && optimalHeight > availableHeightAbove) {
                    actualHeight = availableHeightAbove
                  } else if (
                    !openUpward &&
                    optimalHeight > availableHeightBelow
                  ) {
                    actualHeight = availableHeightBelow
                  }

                  let newTop
                  if (openUpward) {
                    newTop = rect.top - 8 - actualHeight
                  } else {
                    newTop = rect.bottom + 8
                  }
                  if (newTop < 16) newTop = 16

                  setPosition((prev) => ({ ...prev, top: newTop }))
                }
              }, 0)
            }
          }
        }
      }, 10)

      return () => clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setDropdownHeight(0)
    }
  }, [open])

  return (
    <DropdownProvider store={store}>
      <div className={css.container}>
        <Button
          primary={open}
          ref={buttonRef}
          className={clsx(css.button, buttonPadSmall && css.padSmall)}
          height={height}
          action={onOpenInner}
        >
          {children}
        </Button>
        {open && (
          <>
            {createPortal(
              <div
                ref={clickOutsideRef}
                className={css.wrapper}
                style={{
                  position: 'fixed',
                  left: `${position.left}px`,
                  top: `${position.top}px`,
                  zIndex: 10000,
                }}
              >
                <div ref={contentRef} className={css.inner}>
                  {options && (
                    <DropdownMenu
                      title={title}
                      options={options}
                      render={renderOption}
                      onSelect={onSelectInner}
                      onClose={onClose}
                      style={{
                        maxHeight: dropdownHeight
                          ? `${dropdownHeight + 1}px`
                          : 'auto',
                      }}
                    />
                  )}
                  {!options &&
                    React.cloneElement(content as React.ReactElement, {
                      style: {
                        maxHeight: dropdownHeight
                          ? `${dropdownHeight + 1}px`
                          : 'auto',
                      },
                    })}
                </div>
              </div>,
              getDialogElement(),
            )}
          </>
        )}
      </div>
    </DropdownProvider>
  )
}
